import { DOCUMENT } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Inject, Injectable, OnDestroy } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import {
  Observable,
  Subject,
  distinct,
  filter,
  first,
  map,
  mergeMap,
  of,
} from 'rxjs';

import { IconServiceBase } from '../models/icon-service-base.model';
import { SvgIconName } from '../types/types';

@UntilDestroy()
@Injectable({ providedIn: 'root' })
export class IconService implements IconServiceBase, OnDestroy {
  constructor(
    @Inject(DOCUMENT) private readonly document: Document,
    private readonly http: HttpClient,
  ) {
    this.initLoadingBufferHandler();
  }

  private readonly cache: Partial<Record<SvgIconName, SVGElement>> = {};

  private readonly loadingBuffer$ = new Subject<SvgIconName>();

  private readonly svgLoaded$ = new Subject<{
    iconName: SvgIconName;
    svg: SVGElement;
  }>();

  getSvgIcon(svgIconName: SvgIconName): Observable<SVGElement> {
    if (this.cache[svgIconName]) {
      const svg = IconService.cloneSvg(this.cache[svgIconName] as SVGElement);

      return of(svg);
    }

    this.loadingBuffer$.next(svgIconName);

    return this.svgLoaded$.pipe(
      filter(({ iconName }) => iconName === svgIconName),
      map(({ svg }) => IconService.cloneSvg(svg)),
      first(),
    );
  }

  ngOnDestroy(): void {
    this.loadingBuffer$.complete();
    this.svgLoaded$.complete();
  }

  private initLoadingBufferHandler(): void {
    this.loadingBuffer$
      .pipe(
        distinct(),
        mergeMap((iconName) =>
          this.loadSvg(iconName).pipe(
            first(),
            map((svg) => ({ svg, iconName })),
          ),
        ),
        untilDestroyed(this),
      )
      .subscribe({
        next: ({ svg, iconName }) => {
          this.cache[iconName] = svg;

          this.svgLoaded$.next({
            iconName,
            svg,
          });
        },
      });
  }

  private static cloneSvg(svg: SVGElement): SVGElement {
    return svg.cloneNode(true) as SVGElement;
  }

  private loadSvg(svgIcon: SvgIconName): Observable<SVGElement> {
    return this.http
      .get(`assets/icons/${svgIcon}.svg`, {
        responseType: 'text',
      })
      .pipe(map((text) => this.composeSvgElement(text)));
  }

  private composeSvgElement(svgText: string): SVGElement {
    const div = this.document.createElement('div');

    div.innerHTML = svgText;

    return div.querySelector('svg') as SVGElement;
  }
}
